import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { I18nService } from 'i18n';
import { CommunityLanguageCheckService } from '../../services/community-language-check.service';

/**
 * The one question of §7.3: "this looks like English, but it is about to be
 * saved as German — which is it?".
 *
 * It renders itself off `CommunityLanguageCheckService.request`, so a page only
 * has to place it once; it is invisible until a save path actually runs into a
 * confident mismatch. Modelled on `community-reason-modal` down to the markup,
 * since both are plain Bootstrap dialogs the app draws itself.
 *
 * Saving as the DETECTED language is the primary button: the writer just wrote
 * the text, so the model's reading of it is more likely right than the UI
 * language it inherited. Escape cancels, and cancelling saves nothing at all.
 */
@Component({
  selector: 'app-community-language-confirm-modal',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  template: `
    <ng-container *transloco="let t">
      @if (check.request(); as req) {
        <div class="modal fade show d-block" tabindex="-1" role="dialog" (keydown.escape)="cancel()">
          <div class="modal-dialog" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">{{ t('community.language_check.title') }}</h5>
                <button type="button" class="btn-close" (click)="cancel()"></button>
              </div>
              <div class="modal-body">
                <p class="mb-0">
                  {{ t('community.language_check.message', {
                       detected: languageName(req.detected),
                       expected: languageName(req.expected)
                     }) }}
                </p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary me-auto" (click)="cancel()">
                  {{ t('community.threads.cancel') }}
                </button>
                <button type="button" class="btn btn-outline-secondary" (click)="keepExpected()">
                  {{ t('community.language_check.save_as', { lang: languageName(req.expected) }) }}
                </button>
                <button type="button" class="btn btn-primary" autofocus (click)="useDetected()">
                  {{ t('community.language_check.save_as', { lang: languageName(req.detected) }) }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-backdrop fade show"></div>
      }
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityLanguageConfirmModalComponent {
  readonly check = inject(CommunityLanguageCheckService);

  private readonly i18n = inject(I18nService);

  /** "de" → "Deutsch": the writer is asked about languages, not about codes. */
  languageName(code: string): string {
    return this.i18n.getLanguageName(code);
  }

  useDetected(): void {
    this.check.resolve('detected');
  }

  keepExpected(): void {
    this.check.resolve('expected');
  }

  cancel(): void {
    this.check.resolve('cancel');
  }
}
